import { Component, Input, Output, EventEmitter, OnInit, ViewChild, ElementRef } from '@angular/core';

import { HighlightSearchPipe } from '@Utils';

interface Option {
  label: string;
  value: string;
}

@Component({
  selector: 'app-combobox',
  templateUrl: './combobox.component.html',
  styleUrls: ['./combobox.component.css'],
  imports: [HighlightSearchPipe],
})
export class ComboboxComponent implements OnInit {
  @Input() label = '';
  @Input() options: Option[] | null = null;
  @Output() optionSelected = new EventEmitter<Option>();

  @ViewChild('inputElement') inputElement!: ElementRef;

  filteredOptions: Option[] = [];
  searchText = '';
  isDropdownOpen = false;
  currentFocusIndex = -1;

  ngOnInit(): void {
    this.filteredOptions = this.options ? [...this.options] : [];
  }

  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchText = input.value;

    // Filter options based on input
    this.filteredOptions = this.options
      ? this.options.filter((option) => option.label.toLowerCase().includes(this.searchText.toLowerCase()))
      : [];

    // Open dropdown when typing
    this.isDropdownOpen = true;

    // Reset focus index when input changes
    this.currentFocusIndex = -1;
  }

  onKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.isDropdownOpen = true;
        this.moveFocusDown();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.isDropdownOpen = true;
        this.moveFocusUp();
        break;

      case 'Enter':
        if (this.currentFocusIndex >= 0 && this.currentFocusIndex < this.filteredOptions.length) {
          this.selectOption(this.filteredOptions[this.currentFocusIndex]);
          event.preventDefault();
        }
        break;

      case 'Escape':
        this.isDropdownOpen = false;
        this.currentFocusIndex = -1;
        break;
    }
  }

  onOptionKeyDown(event: KeyboardEvent, option: Option, index: number): void {
    switch (event.key) {
      case 'Enter':
      case ' ': // Space key
        event.preventDefault();
        this.optionClicked(option, index);
        break;

      case 'ArrowDown':
        event.preventDefault();
        this.moveFocusDown();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.moveFocusUp();
        break;

      case 'Escape':
        event.preventDefault();
        this.isDropdownOpen = false;
        this.currentFocusIndex = -1;
        this.inputElement.nativeElement.focus();
        break;
    }
  }

  onButtonKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Enter':
      case ' ': // Space key
        event.preventDefault();
        this.toggleDropdown(event);
        break;
    }
  }

  moveFocusDown(): void {
    if (this.filteredOptions.length > 0) {
      this.currentFocusIndex = (this.currentFocusIndex + 1) % this.filteredOptions.length;
      this.focusOption(this.currentFocusIndex);
    }
  }

  moveFocusUp(): void {
    if (this.filteredOptions.length > 0) {
      this.currentFocusIndex = (this.currentFocusIndex - 1 + this.filteredOptions.length) % this.filteredOptions.length;
      this.focusOption(this.currentFocusIndex);
    }
  }

  focusOption(index: number): void {
    // Use setTimeout to ensure the DOM is updated
    setTimeout(() => {
      const optionId = this.getOptionId(index);
      const optionElement = document.getElementById(optionId);
      if (optionElement) {
        optionElement.focus();
      }
    });
  }

  selectOption(option: Option): void {
    this.searchText = option.label;
    this.isDropdownOpen = false;
    this.currentFocusIndex = -1;

    // Emit the selected option to the parent component
    this.optionSelected.emit(option);

    // Return focus to the input element
    this.inputElement.nativeElement.focus();
  }

  toggleDropdown(event: Event): void {
    this.filteredOptions = this.options ? [...this.options] : [];
    event.preventDefault();
    this.isDropdownOpen = !this.isDropdownOpen;

    if (this.isDropdownOpen) {
      // Focus the input when dropdown opens
      this.inputElement.nativeElement.focus();
    } else {
      this.currentFocusIndex = -1;
    }
  }

  // Create a DOM-safe ID by removing spaces and special characters
  getSafeId(text: string): string {
    return text.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  optionClicked(option: Option, index: number): void {
    this.currentFocusIndex = index;
    this.selectOption(option);
  }

  getOptionId(index: number): string {
    return `option-${this.getSafeId(this.label)}-${index}`;
  }
}
